import Phaser from 'phaser';
import type { LandSlimeConfig, ProjectileEffectType } from './BossConfig';

// Снаряд ОТ врага В игрока. Симметричен Bullet, но коллизит с Player, не с Enemy.
// Не использует physics body — движется вручную через update(deltaMs).
//
// Две траектории:
//   Прямая (default): летит по нормированному вектору в зафиксированную точку.
//   Дуга (useArc): vy += gravity * dt, x += vx*dt, y += vy*dt. Возврат к
//     startY (vy > 0 в этот момент) → hasLanded=true, GameScene спавнит
//     landSlimeConfig зону на промахе.
//
// Рендер: цветной круг (Arc) ИЛИ анимированный спрайт. Если в конструктор передан
// textureKey и текстура загружена — используется Sprite, иначе fallback на Arc.
// Коллизия в обоих случаях радиальная по полю `radius` (НЕ по реальным размерам
// спрайта) — для предсказуемого хитбокса независимо от визуала.
export class EnemyProjectile {
  public sprite: Phaser.GameObjects.Arc | Phaser.GameObjects.Sprite;
  public x: number;
  public y: number;
  public radius: number;
  public isDead = false;
  public hasLanded = false;
  public readonly effectType: ProjectileEffectType;
  public readonly effectDurationMs: number;
  // Используется только для effectType 'slow'. 0.45 = 45% базовой скорости.
  public readonly slowFactor: number;
  // Конфиг лужи на месте приземления — GameScene читает после hasLanded=true.
  public readonly landSlimeConfig?: LandSlimeConfig;

  // Прямая траектория.
  private dirX: number;
  private dirY: number;
  private speed: number;

  // Параболическая траектория.
  private useArc: boolean;
  private vx = 0;
  private vy = 0;
  private gravity = 0;
  private landY = 0;
  private rotationSpeed: number;

  constructor(
    scene: Phaser.Scene,
    startX: number,
    startY: number,
    targetX: number,
    targetY: number,
    speed: number,
    size: number,
    color: number,
    effectDurationMs: number,
    effectType: ProjectileEffectType = 'block_shooting',
    slowFactor = 1.0,
    textureKey?: string,
    animKey?: string,
    spriteHeightPx?: number,
    useArc = false,
    gravity = 1500,
    arcHeight = 200,
    rotationSpeed = 0,
    landSlimeConfig?: LandSlimeConfig,
  ) {
    this.x = startX;
    this.y = startY;
    this.radius = size;
    this.speed = speed;
    this.effectDurationMs = effectDurationMs;
    this.effectType = effectType;
    this.slowFactor = slowFactor;
    this.landSlimeConfig = landSlimeConfig;
    this.useArc = useArc;
    this.rotationSpeed = rotationSpeed;

    if (useArc) {
      // Расчёт под параболу с апексом arcHeight выше startY, возвращение
      // к startY в targetX. vy(0) — начальная вертикальная скорость (вверх,
      // отрицательная в координатах Phaser где y растёт вниз).
      // Из v² = 2*g*h: |vy| = sqrt(2 * g * h)
      // Время полёта T до возврата на startY: T = 2 * |vy| / g
      // vx = (targetX - startX) / T (со знаком направления).
      this.gravity = gravity;
      this.landY = startY;
      this.vy = -Math.sqrt(2 * gravity * Math.max(1, arcHeight));
      const T = (-2 * this.vy) / gravity;
      const dxToTarget = targetX - startX;
      this.vx = dxToTarget / T;
      // dirX/dirY для совместимости с offscreen-check в GameScene.
      this.dirX = Math.sign(dxToTarget) || 0;
      this.dirY = 0;
    } else {
      const dx = targetX - startX;
      const dy = targetY - startY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      this.dirX = dist > 0 ? dx / dist : 0;
      this.dirY = dist > 0 ? dy / dist : 0;
    }

    if (textureKey && scene.textures.exists(textureKey)) {
      const sp = scene.add.sprite(startX, startY, textureKey);
      if (animKey && scene.anims.exists(animKey)) sp.play(animKey);
      const targetH = spriteHeightPx ?? size * 2;
      const aspect = sp.width / sp.height;
      sp.setDisplaySize(targetH * aspect, targetH);
      // Спрайт нарисован мордой ВПРАВО. dirX < 0 → летит влево → флип.
      // Для камня (симметричный) флип не задействован визуально, но не вредит.
      sp.setFlipX(this.dirX < 0);
      sp.setDepth(50);
      this.sprite = sp;
    } else {
      const arc = scene.add.circle(startX, startY, size, color);
      arc.setDepth(50);
      this.sprite = arc;
    }
  }

  update(deltaMs: number): void {
    if (this.isDead || this.hasLanded) return;
    const dt = deltaMs / 1000;

    if (this.useArc) {
      this.vy += this.gravity * dt;
      this.x += this.vx * dt;
      this.y += this.vy * dt;
      // Приземление: y вернулся к landY (или ниже) на ниспадающей ветке (vy > 0).
      if (this.y >= this.landY && this.vy > 0) {
        this.y = this.landY;
        this.hasLanded = true;
      }
    } else {
      const moveDistance = this.speed * dt;
      this.x += this.dirX * moveDistance;
      this.y += this.dirY * moveDistance;
    }

    this.sprite.setPosition(this.x, this.y);

    if (this.rotationSpeed !== 0) {
      // Phaser sprite/Arc оба поддерживают .rotation (radians).
      this.sprite.rotation += this.rotationSpeed * dt;
    }
  }

  destroy(): void {
    if (this.isDead) return;
    this.isDead = true;
    this.sprite.destroy();
  }
}
